import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth} from "../services/auth";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Eye, EyeOff, Mail } from "lucide-react";
import { FcGoogle } from "react-icons/fc";

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    loginWithGoogle,
  } = useAuth();
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const [authMode, setAuthMode] = useState<"login" | "signup" | "forgotPassword">("login");
  const [formData, setFormData] = useState({ 
    email: "", 
    password: "", 
    name: "" 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /* Commented out unused handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { email, password, name } = formData;

    if (!email || (authMode !== "forgotPassword" && !password)) {
      toast.error("Please fill in all required fields");
      setIsLoading(false);
      return;
    }

    try {
      switch (authMode) {
        case "forgotPassword":
          await resetPassword.createResetRequest(email);
          setAuthMode("login");
          break;
        case "login":
          await login(email, password);
          break;
        case "signup":
          await signup(email, password, name);
          break;
      }
    } catch (err) {
      console.error("Authentication Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFormFields = () => {
    if (authMode === "signup") {
      return (
        <Input
          name="name"
          type="text"
          placeholder="Your Name"
          value={formData.name}
          onChange={handleChange}
          className="mb-4 rounded-xl transition-all duration-300 focus:ring-2 focus:ring-blue-500"
          required
        />
      );
    }
    return null;
  };
  */

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      console.error("Google Sign-In Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center p-4 md:p-8 transition-all duration-500">
      <div className="w-full max-w-6xl grid md:grid-cols-2 bg-white rounded-[32px] overflow-hidden shadow-2xl transform transition-all duration-500 hover:shadow-3xl">
        <div className="p-8 md:p-12 bg-black text-white flex flex-col justify-between transition-all duration-300">
          <h1 className="text-4xl font-serif mb-4 transition-all duration-300 hover:scale-105">
            Speak Clearly, Communicate Confidently
          </h1>
          <p className="text-sm opacity-80 transition-all duration-300 hover:opacity-100">
            Transform your voice recordings with AI-powered filler word removal
          </p>
        </div>

        <div className="p-8 md:p-12 flex flex-col justify-center items-center">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold">Welcome Back</h2>
              <p className="text-gray-600">Sign in to continue to your account</p>
            </div>

            {/* Comment out regular auth form
            <form onSubmit={handleAuth} className="space-y-4">
              <Input
                name="email"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="rounded-xl transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                required
              />
              {authMode !== "forgotPassword" && (
                <div className="relative">
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className="rounded-xl transition-all duration-300 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 transition-colors duration-200 hover:text-gray-700"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              )}
              {renderFormFields()}

              <div className="flex items-center">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(!!checked)}
                  className="mr-2 rounded-md transition-all duration-200"
                />
                <Label htmlFor="rememberMe" className="cursor-pointer">
                  Remember Me
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white py-2 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]" 
                disabled={isLoading}
              >
                {authMode === "login"
                  ? "Login"
                  : authMode === "signup"
                  ? "Sign Up"
                  : "Reset Password"}
              </Button>
            </form>
            */}

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full bg-white text-black border border-gray-300 flex items-center justify-center gap-3 py-3 rounded-xl shadow-md transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg hover:border-gray-400 active:scale-[0.98]"
              disabled={isLoading}
            >
              <FcGoogle className="w-5 h-5 text-red-500" />
              {isLoading ? "Signing in..." : "Continue with Google"}
            </button>

            {/* Comment out additional auth options
            <div className="flex justify-between text-sm mt-6 space-x-4">
              <button
                type="button"
                onClick={() => setAuthMode(authMode === "login" ? "signup" : "login")}
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
              >
                {authMode === "login"
                  ? "Don't have an account? Sign Up"
                  : "Already have an account? Login"}
              </button>
              {authMode === "login" && (
                <button
                  type="button"
                  onClick={() => setAuthMode("forgotPassword")}
                  className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
                >
                  Forgot Password?
                </button>
              )}
            </div>
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;  

